import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { endOfDay, format, subDays, subMonths, subWeeks, subYears } from 'date-fns';
import { hu } from 'date-fns/locale';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceArea } from 'recharts';
import { PiClockCounterClockwiseDuotone } from 'react-icons/pi';

import OptimizationPipelinePopup, {
    OptimizationPipelinePopupHeader,
    PopupDateRangeQuickSelector,
    PopupBody,
    PopupToolbar,
    PopupToolbarGroup,
    PopupToolbarChip,
    PopupExportButtons
} from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopup';

import OptimizationPipelinePopupTable, {
    PopupTableActionButton
} from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopupTable';

import {
    OptimizationPipelineChartFrame,
    OptimizationPipelineChartEmptyState,
    OptimizationPipelineChartKeySelector,
    OptimizationPipelineChartTooltip,
    getChartPortalTooltipProps
} from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelineChart';

import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';
import { getSoftStatusClass } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/colorUtils';
import { parseTsToDate } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/dateTime';
import { buildExportFilename, exportPopupTable } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/exportUtils';
import { STATUS_BADGE_CLASS, STATUS_CELL_CLASS, DYNAMIC_VALIDATION_STATE_META, DYNAMIC_VALIDATION_DETAIL_GROUP_META } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/constantUtils';

const VIEW_SUMMARY = 'summary';
const VIEW_COMPARISON = 'comparison';
const VIEW_DETAILS = 'details';
const VIEW_MONITOR = 'monitor';
const VIEW_ITERATION = 'iteration';
const DEFAULT_DETAILS_GROUP = 'best_values';
const INITIAL_DATE_RANGE = { from: '', to: '', quickRange: 'ALL' };

const TRAJECTORY_DATASET_OPTIONS = [
    { value: 'variables_to_monitor', label: 'Monitor Variables' },
    { value: 'variances', label: 'Variances' },
    { value: 'alarm_variables', label: 'Alarm Variables' },
    { value: 'sequence_finished_tags', label: 'Sequence Tags' }
];

const TRAJECTORY_ROUND_META = {
    base: { label: 'Base', fill: 'rgba(245,158,11,0.12)', stroke: 'rgba(245,158,11,0.32)' },
    best: { label: 'Best', fill: 'rgba(34,197,94,0.12)', stroke: 'rgba(34,197,94,0.32)' }
};

const VARIANCE_BELOW_SIGMA_CHIP_STYLE = { backgroundColor: 'rgba(196,238,208)' };

const DETAIL_GROUP_OPTIONS = [
    'base_values',
    'best_values',
    'variables_to_monitor',
    'variances',
    'sigmas',
    'alarm_variables'
];

function isPlainObject(value) {
    return value && typeof value === 'object' && !Array.isArray(value);
}

function getStatusClass(color, baseClassName) {
    return baseClassName + ' ' + getSoftStatusClass(color);
}

function formatDisplayTimestamp(value) {
    const date = parseTsToDate(value);

    return date ? format(date, 'yyyy.MM.dd. HH:mm:ss', { locale: hu }) : '-';
}

function formatInputDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    return format(date, 'yyyy-MM-dd');
}

function formatDisplayDate(value) {
    const date = parseTsToDate(value);
    return date ? format(date, 'yyyy.MM.dd.', { locale: hu }) : '';
}

function getDateRangeSubtitle(dateRange) {
    const from = formatDisplayDate(dateRange?.from);
    const to = formatDisplayDate(dateRange?.to);

    if (from && to) return 'Showing data from ' + from + ' to ' + to;
    if (from) return 'Showing data from ' + from;
    if (to) return 'Showing data until ' + to;

    return null;
}

function formatNumber(value) {
    if (value === null || value === undefined || value === '') return '-';

    const number = Number(value);

    if (!Number.isFinite(number)) return String(value);

    return number.toLocaleString('hu-HU', {
        maximumFractionDigits: 4
    });
}

function getIterationCount(value, firstValue) {
    const n = Number(value);
    const first = Number(firstValue);

    if (!Number.isFinite(n)) return null;
    if (!Number.isFinite(first)) return n + 1;

    return n - first + 1;
}

function getPayloadIteration(payload) {
    const direct = Number(payload?.iteration);
    if (Number.isFinite(direct)) return direct;

    const match = String(payload?.message || '').match(/iteration:\s*(\d+)/i);
    if (!match) return undefined;

    const parsed = Number(match[1]);
    return Number.isFinite(parsed) ? parsed : undefined;
}

function buildOverallIndexTicks(rows) {
    const iterations = rows.map(row => Number(row?.overallIndex)).filter(Number.isFinite);
    if (!iterations.length) return [];

    const min = Math.min(...iterations);
    const max = Math.max(...iterations);
    const count = iterations.length;
    const step = count > 100 ? 5 : count > 50 ? 2 : 1;
    const ticks = [];

    for (let i = min; i <= max; i += step) ticks.push(i);
    if (!ticks.includes(max)) ticks.push(max);

    return ticks;
}

function formatExportDetailValue(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);

    return String(value);
}

function formatObjectDetailsForExport(value) {
    if (!isPlainObject(value)) return '';

    return Object.entries(value)
        .map(([key, itemValue]) => key + ': ' + formatExportDetailValue(itemValue))
        .join('; ');
}

function getPrettyError(error) {
    return String(error?.message || error || 'Unknown error');
}

function getRows(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (data) return [data];

    return [];
}

function getTrajectoryResourceKey(runId) {
    return runId ? 'edtDynamicCheckTrajectoryData:' + runId : null;
}

function getLatestRow(rows) {
    if (!Array.isArray(rows) || !rows.length) return null;

    return rows.reduce((latest, row) => {
        if (!latest) return row;

        const latestTs = parseTsToDate(latest?.ts)?.getTime?.() || 0;
        const rowTs = parseTsToDate(row?.ts)?.getTime?.() || 0;

        if (rowTs !== latestTs) return rowTs > latestTs ? row : latest;

        const latestId = Number(latest?.id);
        const rowId = Number(row?.id);

        if (Number.isFinite(rowId) && Number.isFinite(latestId)) return rowId > latestId ? row : latest;

        return latest;
    }, null);
}

function getObjectKeys(rows, key) {
    const keys = new Set();

    rows.forEach((row) => {
        const source = row?.[key];

        if (!isPlainObject(source)) return;

        Object.keys(source).forEach(itemKey => keys.add(itemKey));
    });

    return Array.from(keys).sort();
}

function getStateValue(row) {
    return row?.state || (row?.finished === false ? 'NOT_FINISHED' : null);
}

function renderStateBadge(row) {
    const value = getStateValue(row);
    const meta = value ? DYNAMIC_VALIDATION_STATE_META[value] : null;

    if (meta) {
        return <div className={getStatusClass(meta.color, STATUS_BADGE_CLASS)}>{meta.label}</div>;
    }

    return <div className={getStatusClass('gray', STATUS_BADGE_CLASS)}>{value || '-'}</div>;
}

function renderBooleanBadge(value, trueLabel = 'Yes', falseLabel = 'No') {
    if (value === true) return <div className={getStatusClass('green', STATUS_BADGE_CLASS)}>{trueLabel}</div>;
    if (value === false) return <div className={getStatusClass('gray', STATUS_BADGE_CLASS)}>{falseLabel}</div>;

    return <div className={getStatusClass('red', STATUS_BADGE_CLASS)}>Missing</div>;
}

function renderAlarmBadge(value) {
    if (value === true) return <div className={getStatusClass('red', STATUS_BADGE_CLASS)}>Yes</div>;
    if (value === false) return <div className={getStatusClass('green', STATUS_BADGE_CLASS)}>No</div>;

    return <div className={getStatusClass('gray', STATUS_BADGE_CLASS)}>Missing</div>;
}

function renderDiffCell(value, suffix = '') {
    const number = Number(value);

    if (!Number.isFinite(number)) {
        return <div className={getStatusClass('gray', STATUS_CELL_CLASS)}><span className='font-semibold'>-</span></div>;
    }

    if (number > 0) {
        return <div className={getStatusClass('green', STATUS_CELL_CLASS)}><span className='font-semibold'>+{formatNumber(number)}{suffix}</span></div>;
    }

    if (number < 0) {
        return <div className={getStatusClass('yellow', STATUS_CELL_CLASS)}><span className='font-semibold'>{formatNumber(number)}{suffix}</span></div>;
    }

    return <div className={getStatusClass('gray', STATUS_CELL_CLASS)}><span className='font-semibold'>0{suffix}</span></div>;
}

function colorForKey(key) {
    let h = 0;

    for (let i = 0; i < key.length; i++) h = ((h * 31) + key.charCodeAt(i)) % 360;

    return 'hsl(' + h + ',70%,40%)';
}

function getTrajectoryPayload(row) {
    return isPlainObject(row?.payload) ? row.payload : {};
}

function getTrajectoryDatasetSource(row, datasetMode) {
    const payload = getTrajectoryPayload(row);

    if (datasetMode === 'variances') {
        return row?.variances || payload.variances;
    }

    if (datasetMode === 'alarm_variables') {
        return row?.alarm_variables || payload.alarm_variables;
    }

    if (datasetMode === 'sequence_finished_tags') {
        return row?.sequence_finished_tags || payload.sequence_finished_tags;
    }

    return row?.variables_to_monitor || payload.variables_to_monitor;
}

function getTrajectorySigmas(row) {
    const payload = getTrajectoryPayload(row);

    return row?.sigmas || payload.sigmas;
}

function getTrajectoryRows(data) {
    const rows = getRows(data);

    return rows
        .filter(Boolean)
        .sort((a, b) => {
            const idA = Number(a?.id);
            const idB = Number(b?.id);

            if (Number.isFinite(idA) && Number.isFinite(idB)) return idA - idB;

            const dateA = parseTsToDate(a?.ts)?.getTime?.() || 0;
            const dateB = parseTsToDate(b?.ts)?.getTime?.() || 0;

            return dateA - dateB;
        });
}

function getTrajectoryKeys(rows, datasetMode) {
    const keys = new Set();

    rows.forEach((row) => {
        const source = getTrajectoryDatasetSource(row, datasetMode);

        if (!isPlainObject(source)) return;

        Object.keys(source).forEach(key => keys.add(key));
    });

    return Array.from(keys).sort();
}

function getTrajectoryMetaValue(row, key) {
    const payload = getTrajectoryPayload(row);

    return row?.[key] ?? payload?.[key] ?? null;
}

function buildVarianceBelowSigmaKeys(rows) {
    const belowSigma = new Set();

    rows.forEach((row) => {
        const variances = getTrajectoryDatasetSource(row, 'variances');
        const sigmas = getTrajectorySigmas(row);

        if (!isPlainObject(variances) || !isPlainObject(sigmas)) return;

        Object.entries(variances).forEach(([key, varianceValue]) => {
            const variance = Number(varianceValue);
            const sigma = Number(sigmas[key]);

            if (Number.isFinite(variance) && Number.isFinite(sigma) && variance < sigma) {
                belowSigma.add(key);
            } else {
                belowSigma.delete(key);
            }
        });
    });

    return Array.from(belowSigma);
}

function buildTrajectoryChartRows(rows, selectedKeys, datasetMode) {
    const iterationState = { overallCount: 0, base: null, best: null, firstBase: null, firstBest: null };

    return rows.map((row, index) => {
        const payload = getTrajectoryPayload(row);
        const round = String(getTrajectoryMetaValue(row, 'round') || '').toLowerCase();
        const rawIteration = row?.iteration ?? getPayloadIteration(payload);
        const roundIteration = Number(rawIteration);

        if (round === 'base' && Number.isFinite(roundIteration)) {
            if (iterationState.firstBase == null) iterationState.firstBase = roundIteration;
            iterationState.base = getIterationCount(roundIteration, iterationState.firstBase);
        }

        if (round === 'best' && Number.isFinite(roundIteration)) {
            if (iterationState.firstBest == null) iterationState.firstBest = roundIteration;
            iterationState.best = getIterationCount(roundIteration, iterationState.firstBest);
        }

        if (!Number.isFinite(roundIteration)) {
            if (round === 'base') iterationState.base = (Number(iterationState.base) || 0) + 1;
            if (round === 'best') iterationState.best = (Number(iterationState.best) || 0) + 1;
        }

        iterationState.overallCount = (Number(iterationState.base) || 0) + (Number(iterationState.best) || 0) || index + 1;

        const rawTs = row?.ts ?? payload.ts;
        const date = parseTsToDate(rawTs);
        const source = getTrajectoryDatasetSource(row, datasetMode);
        const sigmas = getTrajectorySigmas(row);
        const next = {
            index,
            overallIndex: index,
            overallCount: iterationState.overallCount,
            iteration: Number.isFinite(roundIteration) ? roundIteration : null,
            iterationLabel: Number.isFinite(roundIteration) ? String(roundIteration) : '-',
            baseIteration: iterationState.base,
            bestIteration: iterationState.best,
            runId: row?.run_id ?? payload.run_id ?? null,
            ts: rawTs,
            tsLabel: date ? format(date, 'yyyy.MM.dd. HH:mm:ss', { locale: hu }) : '-',
            round,
            userId: row?.meta?.userId ?? payload?.meta?.userId ?? null,
            finished: getTrajectoryMetaValue(row, 'finished'),
            alarm: getTrajectoryMetaValue(row, 'alarm'),
            converged: getTrajectoryMetaValue(row, 'converged'),
            sequenceTagCount: Object.keys(row?.sequence_finished_tags || payload.sequence_finished_tags || {}).length,
            alarmVariableCount: Object.keys(row?.alarm_variables || payload.alarm_variables || {}).length
        };

        selectedKeys.forEach((key) => {
            const value = Number(isPlainObject(source) ? source[key] : null);

            next[key] = Number.isFinite(value) ? value : null;

            if (datasetMode === 'variances') {
                const sigma = Number(isPlainObject(sigmas) ? sigmas[key] : null);
                next[key + '__sigma'] = Number.isFinite(sigma) ? sigma : null;
            }
        });

        return next;
    });
}

function buildTrajectoryRoundBands(chartRows) {
    if (!chartRows.length) return [];

    const bands = [];
    let startIndex = 0;

    for (let i = 1; i <= chartRows.length; i += 1) {
        const prev = chartRows[i - 1];
        const current = chartRows[i];

        if (!current || current.round !== prev.round) {
            const first = chartRows[startIndex];
            const last = chartRows[i - 1];
            const before = chartRows[startIndex - 1];
            const after = chartRows[i];
            const x1 = before ? (Number(before.overallIndex) + Number(first.overallIndex)) / 2 : Number(first.overallIndex);
            const x2 = after ? (Number(last.overallIndex) + Number(after.overallIndex)) / 2 : Number(last.overallIndex);

            if (first.round) {
                bands.push({
                    x1,
                    x2,
                    round: first.round,
                    meta: TRAJECTORY_ROUND_META[first.round] || null
                });
            }

            startIndex = i;
        }
    }

    return bands;
}

function buildComparisonRows(row) {
    if (!row) return [];

    const keys = new Set();

    Object.keys(row.base_values || {}).forEach(key => keys.add(key));
    Object.keys(row.best_values || {}).forEach(key => keys.add(key));

    return Array.from(keys).sort().map((key) => {
        const base = row.base_values?.[key];
        const best = row.best_values?.[key];

        const baseNumber = Number(base);
        const bestNumber = Number(best);
        const hasNumberDiff = Number.isFinite(baseNumber) && Number.isFinite(bestNumber);
        const diff = hasNumberDiff ? bestNumber - baseNumber : null;
        const diffPercent = hasNumberDiff && baseNumber !== 0 ? (diff / baseNumber) * 100 : null;

        return {
            key,
            base,
            best,
            diff,
            diffPercent
        };
    });
}

function buildDetailsRows(row, groupKey) {
    const source = row?.[groupKey];

    if (!isPlainObject(source)) return [];

    return Object.entries(source).map(([key, value]) => ({
        key,
        value
    }));
}

function buildMonitorChartRows(rows, selectedKeys) {
    return rows
        .filter(row => row?.ts && isPlainObject(row?.variables_to_monitor))
        .map((row) => {
            const next = {
                id: row.id,
                ts: row.ts,
                tsLabel: formatDisplayTimestamp(row.ts),
                state: row.state,
                finished: row.finished,
                alarm: row.alarm,
                converged: row.converged
            };

            selectedKeys.forEach((key) => {
                const value = Number(row.variables_to_monitor?.[key]);

                next[key] = Number.isFinite(value) ? value : null;
            });

            return next;
        });
}

export default function DynamicValidationHistoryPopup(props) {
    const {
        popup,
        closePopup,
        ts,
        getOnDemandResource,
        ensureOnDemandResource
    } = useOptimizationPipeline();

    const chartRef = useRef(null);

    const [dateRange, setDateRange] = useState(INITIAL_DATE_RANGE);
    const [view, setView] = useState(VIEW_SUMMARY);
    const [selectedRunId, setSelectedRunId] = useState(null);
    const [selectedMonitorKeys, setSelectedMonitorKeys] = useState([]);
    const [selectedTrajectoryKeys, setSelectedTrajectoryKeys] = useState([]);
    const [hoveredMonitorKey, setHoveredMonitorKey] = useState(null);
    const [hoveredTrajectoryKey, setHoveredTrajectoryKey] = useState(null);
    const [trajectoryDatasetMode, setTrajectoryDatasetMode] = useState('variables_to_monitor');
    const [trajectoryLineType, setTrajectoryLineType] = useState('step');
    const [showTrajectorySigma, setShowTrajectorySigma] = useState(true);
    const [detailsGroup, setDetailsGroup] = useState(DEFAULT_DETAILS_GROUP);
    const [summarySort, setSummarySort] = useState({ key: 'ts', direction: 'desc' });
    const [comparisonSort, setComparisonSort] = useState({ key: 'key', direction: 'asc' });
    const [detailsSort, setDetailsSort] = useState({ key: 'key', direction: 'asc' });

    const open = !!popup && popup.type === 'DYNAMIC_VALIDATION_HISTORY';

    const edtDynamicCheckLogResource = getOnDemandResource('edtDynamicCheckLogData');
    const edtDynamicCheckLogData = edtDynamicCheckLogResource?.data;

    useEffect(() => {
        if (!open) return;

        setView(VIEW_SUMMARY);
        setSelectedRunId(null);
        setDetailsGroup(DEFAULT_DETAILS_GROUP);
        setHoveredMonitorKey(null);
        setHoveredTrajectoryKey(null);
        setSelectedTrajectoryKeys([]);
        setTrajectoryDatasetMode('variables_to_monitor');
        setTrajectoryLineType('step');
        setShowTrajectorySigma(true);
        setSummarySort({ key: 'ts', direction: 'desc' });
        setComparisonSort({ key: 'key', direction: 'asc' });
        setDetailsSort({ key: 'key', direction: 'asc' });
        ensureOnDemandResource('edtDynamicCheckLogData');
    }, [open, ts]);

    const rows = useMemo(() => {
        return getRows(edtDynamicCheckLogData);
    }, [edtDynamicCheckLogData]);

    const dateBounds = useMemo(() => {
        const dates = rows
            .map((row) => parseTsToDate(row?.ts))
            .filter(Boolean)
            .sort((a, b) => a.getTime() - b.getTime());

        if (!dates.length) return { minDate: null, maxDate: null };

        return {
            minDate: dates[0],
            maxDate: dates[dates.length - 1]
        };
    }, [rows]);

    useEffect(() => {
        if (!dateBounds.minDate || !dateBounds.maxDate) return;

        const nextFrom = formatInputDate(dateBounds.minDate);
        const nextTo = formatInputDate(dateBounds.maxDate);

        setDateRange((prev) => {
            if (prev.from === nextFrom && prev.to === nextTo && prev.quickRange === 'ALL') return prev;
            return { from: nextFrom, to: nextTo, quickRange: 'ALL' };
        });
    }, [dateBounds]);

    const disabledQuickOptions = useMemo(() => {
        const { minDate, maxDate } = dateBounds;

        if (!minDate || !maxDate) {
            return {
                ALL: true,
                '1Y': true,
                '1M': true,
                '1W': true,
                '1D': true
            };
        }

        const minTime = minDate.getTime();

        return {
            ALL: false,
            '1Y': subYears(maxDate, 1).getTime() < minTime,
            '1M': subMonths(maxDate, 1).getTime() < minTime,
            '1W': subWeeks(maxDate, 1).getTime() < minTime,
            '1D': subDays(maxDate, 1).getTime() < minTime
        };
    }, [dateBounds]);

    const filteredRows = useMemo(() => {
        const fromDate = parseTsToDate(dateRange?.from);
        const toDate = parseTsToDate(dateRange?.to);

        if (!fromDate && !toDate) return rows;

        const fromTime = fromDate ? fromDate.getTime() : -Infinity;
        const toTime = toDate ? endOfDay(toDate).getTime() : Infinity;

        return rows.filter((row) => {
            const date = parseTsToDate(row?.ts);
            if (!date) return false;

            const time = date.getTime();
            return time >= fromTime && time <= toTime;
        });
    }, [rows, dateRange]);

    const monitorKeys = useMemo(() => {
        return getObjectKeys(filteredRows, 'variables_to_monitor');
    }, [filteredRows]);

    const latestRun = useMemo(() => {
        return getLatestRow(filteredRows);
    }, [filteredRows]);

    useEffect(() => {
        if (!open) return;
        if (selectedRunId || !latestRun?.id) return;

        setSelectedRunId(latestRun.id);
    }, [open, latestRun, selectedRunId]);

    useEffect(() => {
        if (!open) return;
        if (selectedMonitorKeys.length || !monitorKeys.length) return;

        setSelectedMonitorKeys(monitorKeys.slice(0, 4));
    }, [open, monitorKeys, selectedMonitorKeys.length]);

    const selectedRun = useMemo(() => {
        return filteredRows.find(row => row.id === selectedRunId) || latestRun || null;
    }, [filteredRows, selectedRunId, latestRun]);

    const selectedRunIsLatest = !!selectedRun && !!latestRun && selectedRun.id === latestRun.id;

    const trajectoryResourceKey = getTrajectoryResourceKey(selectedRun?.id);
    const trajectoryResource = getOnDemandResource(trajectoryResourceKey);
    const trajectoryRows = useMemo(() => {
        return getTrajectoryRows(trajectoryResource?.data);
    }, [trajectoryResource?.data]);

    const trajectoryKeys = useMemo(() => {
        return getTrajectoryKeys(trajectoryRows, trajectoryDatasetMode);
    }, [trajectoryRows, trajectoryDatasetMode]);

    const trajectoryVarianceBelowSigmaKeys = useMemo(() => {
        return buildVarianceBelowSigmaKeys(trajectoryRows);
    }, [trajectoryRows]);

    useEffect(() => {
        if (!open || !selectedRun?.id) return;
        if (view !== VIEW_ITERATION) return;

        ensureOnDemandResource(getTrajectoryResourceKey(selectedRun.id));
    }, [open, selectedRun?.id, view, ensureOnDemandResource]);

    useEffect(() => {
        if (!open) return;

        setSelectedTrajectoryKeys([]);
        setHoveredTrajectoryKey(null);
    }, [open, selectedRun?.id, trajectoryDatasetMode]);

    useEffect(() => {
        if (!open || view !== VIEW_ITERATION) return;
        if (selectedTrajectoryKeys.length || !trajectoryKeys.length) return;

        setSelectedTrajectoryKeys(trajectoryKeys.slice(0, 6));
    }, [open, view, trajectoryKeys, selectedTrajectoryKeys.length]);

    const comparisonRows = useMemo(() => {
        return buildComparisonRows(selectedRun);
    }, [selectedRun]);

    const detailsRows = useMemo(() => {
        return buildDetailsRows(selectedRun, detailsGroup);
    }, [selectedRun, detailsGroup]);

    const monitorChartRows = useMemo(() => {
        return buildMonitorChartRows(filteredRows, selectedMonitorKeys);
    }, [filteredRows, selectedMonitorKeys]);

    const trajectoryChartRows = useMemo(() => {
        return buildTrajectoryChartRows(trajectoryRows, selectedTrajectoryKeys, trajectoryDatasetMode);
    }, [trajectoryRows, selectedTrajectoryKeys, trajectoryDatasetMode]);

    const trajectoryRoundBands = useMemo(() => {
        return buildTrajectoryRoundBands(trajectoryChartRows);
    }, [trajectoryChartRows]);

    const trajectoryOverallIndexTicks = useMemo(() => {
        return buildOverallIndexTicks(trajectoryChartRows);
    }, [trajectoryChartRows]);

    const isMonitorSeriesHighlighted = useCallback((key) => {
        if (!hoveredMonitorKey) return true;
        return hoveredMonitorKey === key;
    }, [hoveredMonitorKey]);

    const getMonitorSeriesOpacity = useCallback((key) => (
        isMonitorSeriesHighlighted(key) ? 1 : 0.18
    ), [isMonitorSeriesHighlighted]);

    const getMonitorSeriesStrokeWidth = useCallback((key) => {
        if (!hoveredMonitorKey) return 2;
        return isMonitorSeriesHighlighted(key) ? 2 : 1;
    }, [hoveredMonitorKey, isMonitorSeriesHighlighted]);

    const isTrajectorySeriesHighlighted = useCallback((key) => {
        if (!hoveredTrajectoryKey) return true;
        return hoveredTrajectoryKey === key;
    }, [hoveredTrajectoryKey]);

    const getTrajectorySeriesOpacity = useCallback((key) => (
        isTrajectorySeriesHighlighted(key) ? 1 : 0.18
    ), [isTrajectorySeriesHighlighted]);

    const getTrajectorySeriesStrokeWidth = useCallback((key) => {
        if (!hoveredTrajectoryKey) return 2;
        return isTrajectorySeriesHighlighted(key) ? 2 : 1;
    }, [hoveredTrajectoryKey, isTrajectorySeriesHighlighted]);

    const getTrajectoryVarianceChipStyle = useCallback((key) => {
        if (trajectoryDatasetMode !== 'variances') return null;

        return trajectoryVarianceBelowSigmaKeys.includes(key) ? VARIANCE_BELOW_SIGMA_CHIP_STYLE : null;
    }, [trajectoryDatasetMode, trajectoryVarianceBelowSigmaKeys]);

    const isLoading = !!edtDynamicCheckLogResource?.loading;
    const errorMessage = edtDynamicCheckLogResource?.error;
    const isTrajectoryLoading = !!trajectoryResource?.loading;
    const trajectoryErrorMessage = trajectoryResource?.error;
    const loaded = !isLoading && !errorMessage && edtDynamicCheckLogData != null;

    const openIterationsView = useCallback((row) => {
        if (!row?.id) return;

        setSelectedRunId(row.id);
        setView(VIEW_ITERATION);
        ensureOnDemandResource(getTrajectoryResourceKey(row.id));
    }, [ensureOnDemandResource]);

    const renderDetailsButton = (row, groupKey) => {
        const groupMeta = DYNAMIC_VALIDATION_DETAIL_GROUP_META[groupKey];
        const active = selectedRun?.id === row.id && detailsGroup === groupKey && view === VIEW_DETAILS;

        return (
            <PopupTableActionButton
                label='Details'
                active={active}
                title={groupMeta?.label || groupKey}
                onClick={() => {
                    setSelectedRunId(row.id);
                    setDetailsGroup(groupKey);
                    setView(VIEW_DETAILS);
                }}
            />
        );
    };

    const summaryTableConfig = useMemo(() => ({
        tableMinWidth: 1080,
        sort: { key: 'ts', direction: 'desc' },
        states: {
            loadingTitle: 'Loading EDT Dynamic Check history...',
            emptyTitle: 'No EDT Dynamic Check history data available',
            errorTitle: 'Failed to load EDT Dynamic Check history'
        },
        columns: [
            {
                key: 'selected',
                label: '',
                minWidth: 74,
                exportable: false,
                renderCell: ({ row }) => (
                    <PopupTableActionButton
                        label='View'
                        active={selectedRun?.id === row.id && view === VIEW_COMPARISON}
                        onClick={() => {
                            setSelectedRunId(row.id);
                            setView(VIEW_COMPARISON);
                        }}
                    />
                )
            },
            {
                key: 'trajectory',
                label: '',
                minWidth: 74,
                exportable: false,
                renderCell: ({ row }) => (
                    <PopupTableActionButton
                        label='Chart'
                        active={selectedRun?.id === row.id && view === VIEW_ITERATION}
                        onClick={() => openIterationsView(row)}
                    />
                )
            },
            {
                key: 'ts',
                label: 'Time',
                exportLabel: 'Time',
                minWidth: 170,
                sortable: true,
                exportValue: row => formatDisplayTimestamp(row.ts),
                renderCell: ({ row }) => formatDisplayTimestamp(row.ts)
            },
            {
                key: 'state',
                label: 'State',
                exportLabel: 'State',
                minWidth: 130,
                sortable: true,
                exportValue: row => DYNAMIC_VALIDATION_STATE_META[row?.state]?.label || row?.state || (row?.finished === false ? DYNAMIC_VALIDATION_STATE_META.NOT_FINISHED.label : '-'),
                renderCell: ({ row }) => renderStateBadge(row)
            },
            {
                key: 'finished',
                label: 'Finished',
                exportLabel: 'Finished',
                minWidth: 100,
                align: 'center',
                sortable: true,
                exportValue: row => row.finished === true ? 'Yes' : row.finished === false ? 'No' : 'Missing',
                renderCell: ({ row }) => renderBooleanBadge(row.finished)
            },
            {
                key: 'converged',
                label: 'Converged',
                exportLabel: 'Converged',
                minWidth: 110,
                align: 'center',
                sortable: true,
                exportValue: row => row.converged === true ? 'Yes' : row.converged === false ? 'No' : 'Missing',
                renderCell: ({ row }) => renderBooleanBadge(row.converged)
            },
            {
                key: 'alarm',
                label: 'Alarm',
                exportLabel: 'Alarm',
                minWidth: 90,
                align: 'center',
                sortable: true,
                exportValue: row => row.alarm === true ? 'Yes' : row.alarm === false ? 'No' : 'Missing',
                renderCell: ({ row }) => renderAlarmBadge(row.alarm)
            },
            {
                key: 'baseValues',
                label: 'Base Values',
                exportLabel: 'Base Values',
                minWidth: 110,
                align: 'center',
                sortable: false,
                getSortValue: row => Object.keys(row.base_values || {}).length,
                exportValue: row => formatObjectDetailsForExport(row.base_values),
                renderCell: ({ row }) => renderDetailsButton(row, 'base_values')
            },
            {
                key: 'bestValues',
                label: 'Best Values',
                exportLabel: 'Best Values',
                minWidth: 110,
                align: 'center',
                sortable: false,
                getSortValue: row => Object.keys(row.best_values || {}).length,
                exportValue: row => formatObjectDetailsForExport(row.best_values),
                renderCell: ({ row }) => renderDetailsButton(row, 'best_values')
            },
            {
                key: 'monitorVars',
                label: 'Monitor Variables',
                exportLabel: 'Monitor Variables',
                minWidth: 120,
                align: 'center',
                sortable: false,
                getSortValue: row => Object.keys(row.variables_to_monitor || {}).length,
                exportValue: row => formatObjectDetailsForExport(row.variables_to_monitor),
                renderCell: ({ row }) => renderDetailsButton(row, 'variables_to_monitor')
            },
            {
                key: 'alarmVars',
                label: 'Alarm Variables',
                exportLabel: 'Alarm Variables',
                minWidth: 110,
                align: 'center',
                sortable: false,
                getSortValue: row => Object.keys(row.alarm_variables || {}).length,
                exportValue: row => formatObjectDetailsForExport(row.alarm_variables),
                renderCell: ({ row }) => renderDetailsButton(row, 'alarm_variables')
            }
        ]
    }), [selectedRun, detailsGroup, view, openIterationsView]);

    const comparisonTableConfig = useMemo(() => ({
        tableMinWidth: 760,
        sort: { key: 'key', direction: 'asc' },
        states: {
            emptyTitle: 'No base / best values available'
        },
        columns: [
            {
                key: 'key',
                label: 'Variable',
                exportLabel: 'Variable',
                minWidth: 220,
                sortable: true,
                renderCell: ({ row }) => <span>{row.key}</span>
            },
            {
                key: 'base',
                label: 'Base',
                exportLabel: 'Base',
                minWidth: 130,
                align: 'right',
                sortable: true,
                exportValue: row => row.base,
                renderCell: ({ row }) => formatNumber(row.base)
            },
            {
                key: 'best',
                label: 'Best',
                exportLabel: 'Best',
                minWidth: 130,
                align: 'right',
                sortable: true,
                exportValue: row => row.best,
                renderCell: ({ row }) => formatNumber(row.best)
            },
            {
                key: 'diff',
                label: 'Difference',
                exportLabel: 'Difference',
                minWidth: 130,
                align: 'right',
                sortable: true,
                exportValue: row => row.diff,
                renderCell: ({ row }) => renderDiffCell(row.diff)
            },
            {
                key: 'diffPercent',
                label: 'Difference %',
                exportLabel: 'Difference %',
                minWidth: 130,
                align: 'right',
                sortable: true,
                exportValue: row => row.diffPercent,
                renderCell: ({ row }) => renderDiffCell(row.diffPercent, '%')
            }
        ]
    }), []);

    const detailsTableConfig = useMemo(() => ({
        tableMinWidth: 520,
        sort: { key: 'key', direction: 'asc' },
        states: {
            emptyTitle: 'No details available'
        },
        columns: [
            {
                key: 'key',
                label: 'Variable',
                exportLabel: 'Variable',
                minWidth: 260,
                sortable: true,
                renderCell: ({ row }) => <span>{row.key}</span>
            },
            {
                key: 'value',
                label: 'Value',
                exportLabel: 'Value',
                minWidth: 180,
                align: 'right',
                sortable: true,
                exportValue: row => row.value,
                renderCell: ({ row }) => formatNumber(row.value)
            }
        ]
    }), []);

    const TooltipContent = ({ active, payload }) => {
        if (!active || !payload?.length) return null;

        const point = payload[0].payload;

        return (
            <OptimizationPipelineChartTooltip title={point.tsLabel} minWidth={220} maxWidth={380}>
                <div>State: {point.state || '-'}</div>
                <div>Finished: {point.finished ? 'Yes' : 'No'}</div>
                <div>Converged: {point.converged ? 'Yes' : 'No'}</div>
                <div>Alarm: {point.alarm ? 'Yes' : 'No'}</div>
                {payload.map(entry => <div key={entry.dataKey}>{entry.name}: {formatNumber(entry.value)}</div>)}
            </OptimizationPipelineChartTooltip>
        );
    };

    const monitorTooltipProps = getChartPortalTooltipProps(TooltipContent, chartRef);

    const TrajectoryTooltipContent = ({ active, payload }) => {
        if (!active || !payload?.length) return null;

        const point = payload[0].payload;

        return (
            <OptimizationPipelineChartTooltip title={'Iteration count ' + (point.overallCount ?? '-')} minWidth={240} maxWidth={420}>
                <div>Time: {point.tsLabel}</div>
                <div>Run ID: {point.runId || '-'}</div>
                <div>Round: {TRAJECTORY_ROUND_META[point.round]?.label || point.round || '-'}</div>
                <div>Round iteration: {point.iteration ?? '-'}</div>
                <div>Base iteration count: {point.baseIteration ?? '-'}</div>
                <div>Best iteration count: {point.bestIteration ?? '-'}</div>
                <div>Overall index: {point.overallIndex ?? '-'}</div>
                <div>Finished: {point.finished === true ? 'Yes' : point.finished === false ? 'No' : '-'}</div>
                <div>Converged: {point.converged === true ? 'Yes' : point.converged === false ? 'No' : '-'}</div>
                <div>Alarm: {point.alarm === true ? 'Yes' : point.alarm === false ? 'No' : '-'}</div>
                <div>Sequence tags: {point.sequenceTagCount}</div>
                <div>Alarm variables: {point.alarmVariableCount}</div>
                <div>User ID: {point.userId || '-'}</div>
                {payload.map(entry => <div key={entry.dataKey}>{entry.name}: {formatNumber(entry.value)}</div>)}
            </OptimizationPipelineChartTooltip>
        );
    };

    const trajectoryTooltipProps = getChartPortalTooltipProps(TrajectoryTooltipContent, chartRef);

    const trajectoryExportConfig = useMemo(() => {
        const columns = [
            { key: 'overallIndex', label: 'Overall index', sortable: true },
            { key: 'overallCount', label: 'Iteration count', sortable: true },
            { key: 'ts', label: 'Time', sortable: true, exportValue: row => row.tsLabel },
            { key: 'runId', label: 'Run ID', sortable: true },
            { key: 'round', label: 'Round', sortable: true },
            { key: 'iteration', label: 'Round iteration', sortable: true },
            { key: 'baseIteration', label: 'Base iteration count', sortable: true },
            { key: 'bestIteration', label: 'Best iteration count', sortable: true },
            { key: 'finished', label: 'Finished', sortable: true },
            { key: 'converged', label: 'Converged', sortable: true },
            { key: 'alarm', label: 'Alarm', sortable: true }
        ];

        selectedTrajectoryKeys.forEach((key) => {
            columns.push({
                key,
                label: key,
                sortable: true,
                exportValue: row => row[key]
            });

            if (trajectoryDatasetMode === 'variances' && showTrajectorySigma) {
                columns.push({
                    key: key + '__sigma',
                    label: key + ' sigma',
                    sortable: true,
                    exportValue: row => row[key + '__sigma']
                });
            }
        });

        return { columns, sort: { key: 'overallIndex', direction: 'asc' } };
    }, [selectedTrajectoryKeys, trajectoryDatasetMode, showTrajectorySigma]);

    const exportDisabled = isLoading || !!errorMessage || (
        (view === VIEW_SUMMARY && !filteredRows.length) ||
        (view === VIEW_COMPARISON && !comparisonRows.length) ||
        (view === VIEW_DETAILS && !selectedRun) ||
        (view === VIEW_ITERATION && (!trajectoryChartRows.length || !selectedTrajectoryKeys.length))
    );

    const handleExport = (formatType) => {
        if (view === VIEW_SUMMARY) {
            exportPopupTable({
                tableConfig: summaryTableConfig,
                rows: filteredRows,
                sort: summarySort,
                filename: buildExportFilename('dynamic-validation-history-summary', ts),
                format: formatType
            });
        }

        if (view === VIEW_COMPARISON) {
            exportPopupTable({
                tableConfig: comparisonTableConfig,
                rows: comparisonRows,
                sort: comparisonSort,
                filename: buildExportFilename('dynamic-validation-history-base-vs-best', selectedRun?.ts || ts),
                format: formatType
            });
        }

        if (view === VIEW_DETAILS) {
            exportPopupTable({
                tableConfig: detailsTableConfig,
                rows: detailsRows,
                sort: detailsSort,
                filename: buildExportFilename('dynamic-validation-history-' + (DYNAMIC_VALIDATION_DETAIL_GROUP_META[detailsGroup]?.filename || 'details'), selectedRun?.ts || ts),
                format: formatType
            });
        }

        if (view === VIEW_ITERATION) {
            exportPopupTable({
                tableConfig: trajectoryExportConfig,
                rows: trajectoryChartRows,
                filename: buildExportFilename('dynamic-validation-history-iterations-' + trajectoryDatasetMode, selectedRun?.ts || ts),
                format: formatType
            });
        }
    };

    return (
        <OptimizationPipelinePopup open={open} onClose={closePopup} width={popup?.width || '1500px'} minWidth={popup?.minWidth || 700} maxWidth={popup?.maxWidth || 1500} className={popup?.className} overflowVisible>

            <OptimizationPipelinePopupHeader title='EDT Dynamic Check History' subtitle={getDateRangeSubtitle(dateRange) || ('Timestamp: ' + formatDisplayTimestamp(ts))} icon={PiClockCounterClockwiseDuotone} onClose={closePopup}>
                <div className='flex items-center gap-8'>
                    <PopupDateRangeQuickSelector value={dateRange} onChange={setDateRange} minDate={dateBounds.minDate} maxDate={dateBounds.maxDate} disabledQuickOptions={disabledQuickOptions} />
                    <PopupExportButtons disabled={exportDisabled || view === VIEW_MONITOR} onExcel={() => handleExport('excel')} onCsv={() => handleExport('csv')} />
                </div>
            </OptimizationPipelinePopupHeader>

            <PopupToolbar className='flex-col items-stretch'>
                <div className='w-full flex flex-wrap items-center justify-between gap-10'>
                    <PopupToolbarGroup label='View:'>
                        <PopupToolbarChip label='Summary' active={view === VIEW_SUMMARY} onClick={() => { setView(VIEW_SUMMARY); }} />
                        <PopupToolbarChip label='Base - Best' active={view === VIEW_COMPARISON} disabled={!selectedRun} onClick={() => { setView(VIEW_COMPARISON); }} />
                        <PopupToolbarChip label='Details' active={view === VIEW_DETAILS} disabled={!selectedRun} onClick={() => { setDetailsGroup(detailsGroup || DEFAULT_DETAILS_GROUP); setView(VIEW_DETAILS); }} />
                        <PopupToolbarChip label='Monitor Chart' active={view === VIEW_MONITOR} disabled={!monitorKeys.length} onClick={() => { setView(VIEW_MONITOR); }} />
                        <PopupToolbarChip label='Iterations' active={view === VIEW_ITERATION} disabled={!selectedRun} onClick={() => { setView(VIEW_ITERATION); }} />
                    </PopupToolbarGroup>

                    <div className='text-11 text-gray-500'>
                        {loaded ? filteredRows.length + ' / ' + rows.length + ' run(s)' : ''}
                    </div>
                </div>

                {view === VIEW_MONITOR && (
                    <div className='w-full'>
                        <OptimizationPipelineChartKeySelector
                            className='w-full'
                            options={monitorKeys}
                            value={selectedMonitorKeys}
                            label='Search monitored variables'
                            placeholder='Search and select variables'
                            getColor={colorForKey}
                            onChange={setSelectedMonitorKeys}
                            onHoverKeyChange={setHoveredMonitorKey}
                        />
                    </div>
                )}

                {view === VIEW_ITERATION && (
                    <div className='w-full flex flex-col gap-8'>
                        <OptimizationPipelineChartKeySelector
                            className='w-full'
                            options={trajectoryKeys}
                            value={selectedTrajectoryKeys}
                            label='Search iteration variables'
                            placeholder='Search and select variables'
                            getColor={colorForKey}
                            getChipStyle={getTrajectoryVarianceChipStyle}
                            onChange={setSelectedTrajectoryKeys}
                            onHoverKeyChange={setHoveredTrajectoryKey}
                        />

                        <div className='w-full flex flex-wrap items-center justify-between gap-10'>
                            <PopupToolbarGroup label='Dataset:'>
                                {TRAJECTORY_DATASET_OPTIONS.map(option => (
                                    <PopupToolbarChip
                                        key={option.value}
                                        label={option.label}
                                        active={trajectoryDatasetMode === option.value}
                                        onClick={() => { setTrajectoryDatasetMode(option.value); }}
                                    />
                                ))}
                            </PopupToolbarGroup>

                            <PopupToolbarGroup label='Line:'>
                                <PopupToolbarChip label={trajectoryLineType === 'step' ? 'Interpolated' : 'Step'} onClick={() => setTrajectoryLineType(prev => prev === 'step' ? 'monotone' : 'step')} />
                            </PopupToolbarGroup>

                            {trajectoryDatasetMode === 'variances' && (
                                <PopupToolbarGroup label='Sigma:'>
                                    <PopupToolbarChip label={showTrajectorySigma ? 'Hide' : 'Show'} active={showTrajectorySigma} onClick={() => setShowTrajectorySigma(prev => !prev)} />
                                </PopupToolbarGroup>
                            )}
                        </div>
                    </div>
                )}

                {view === VIEW_DETAILS && (
                    <PopupToolbarGroup label='Details:'>
                        {DETAIL_GROUP_OPTIONS.map((groupKey) => (
                            <PopupToolbarChip
                                key={groupKey}
                                label={DYNAMIC_VALIDATION_DETAIL_GROUP_META[groupKey]?.label || groupKey}
                                active={detailsGroup === groupKey}
                                disabled={!selectedRun}
                                onClick={() => { setDetailsGroup(groupKey); }}
                            />
                        ))}
                    </PopupToolbarGroup>
                )}

                {(view === VIEW_COMPARISON || view === VIEW_DETAILS || view === VIEW_ITERATION) && selectedRun && (
                    <div className='w-full flex flex-wrap items-center gap-8 text-11 text-gray-500'>
                        <span>Selected run:</span>
                        <span className='font-semibold text-gray-900'>{formatDisplayTimestamp(selectedRun.ts)}</span>
                        {selectedRunIsLatest ? <span className='opacity-75'>Latest</span> : null}
                        {renderStateBadge(selectedRun)}

                        {view === VIEW_DETAILS && detailsGroup && (
                            <>
                                <span className='opacity-50'>/</span>
                                <span>{DYNAMIC_VALIDATION_DETAIL_GROUP_META[detailsGroup]?.label || detailsGroup}</span>
                            </>
                        )}

                        {view === VIEW_ITERATION && (
                            <>
                                <span className='opacity-50'>/</span>
                                <span>{TRAJECTORY_DATASET_OPTIONS.find(option => option.value === trajectoryDatasetMode)?.label || trajectoryDatasetMode}</span>
                                <span className='opacity-50'>/</span>
                                <span>{trajectoryRows.length} iteration(s)</span>
                                {trajectoryRoundBands.map((band) => (
                                    <span key={band.round} className='inline-flex items-center gap-4'>
                                        <span className='inline-block w-10 h-10 rounded-sm' style={{ backgroundColor: band.meta?.fill || 'rgba(148,163,184,0.16)', border: '1px solid ' + (band.meta?.stroke || 'rgba(148,163,184,0.32)') }} />
                                        <span>{band.meta?.label || band.round}</span>
                                    </span>
                                ))}
                            </>
                        )}
                    </div>
                )}
            </PopupToolbar>

            <PopupBody padding={false} scroll>
                {view === VIEW_SUMMARY && (
                    <OptimizationPipelinePopupTable
                        tableConfig={summaryTableConfig}
                        rows={filteredRows}
                        loading={isLoading}
                        error={errorMessage ? getPrettyError(errorMessage) : null}
                        rowKey='id'
                        minHeight={360}
                        sort={summarySort}
                        onSortChange={setSummarySort}
                    />
                )}

                {view === VIEW_COMPARISON && (
                    <OptimizationPipelinePopupTable
                        tableConfig={comparisonTableConfig}
                        rows={comparisonRows}
                        loading={isLoading}
                        error={errorMessage ? getPrettyError(errorMessage) : null}
                        rowKey='key'
                        minHeight={360}
                        sort={comparisonSort}
                        onSortChange={setComparisonSort}
                    />
                )}

                {view === VIEW_DETAILS && (
                    <OptimizationPipelinePopupTable
                        tableConfig={detailsTableConfig}
                        rows={detailsRows}
                        loading={isLoading}
                        error={errorMessage ? getPrettyError(errorMessage) : null}
                        rowKey='key'
                        minHeight={360}
                        sort={detailsSort}
                        onSortChange={setDetailsSort}
                    />
                )}

                {view === VIEW_MONITOR && (
                    <div className='w-full p-20'>
                        {isLoading ? (
                            <OptimizationPipelineChartEmptyState>Loading EDT Dynamic Check history...</OptimizationPipelineChartEmptyState>
                        ) : errorMessage ? (
                            <OptimizationPipelineChartEmptyState>Failed to load EDT Dynamic Check history: {getPrettyError(errorMessage)}</OptimizationPipelineChartEmptyState>
                        ) : !monitorChartRows.length || !selectedMonitorKeys.length ? (
                            <OptimizationPipelineChartEmptyState>No monitored values available</OptimizationPipelineChartEmptyState>
                        ) : (
                            <OptimizationPipelineChartFrame ref={chartRef} height={520} overflowVisible>
                                <LineChart data={monitorChartRows} margin={{ top: 10, left: 10, right: 10, bottom: 10 }}>
                                    <CartesianGrid strokeDasharray='3 3' />
                                    <XAxis dataKey='tsLabel' interval='preserveStartEnd' minTickGap={28} />
                                    <YAxis />
                                    <Tooltip {...monitorTooltipProps} />

                                    {selectedMonitorKeys.map(key => (
                                        <Line key={key} dataKey={key} name={key} type='monotone' stroke={colorForKey(key)} strokeWidth={getMonitorSeriesStrokeWidth(key)} strokeOpacity={getMonitorSeriesOpacity(key)} dot={false} connectNulls isAnimationActive={false} />
                                    ))}
                                </LineChart>
                            </OptimizationPipelineChartFrame>
                        )}
                    </div>
                )}

                {view === VIEW_ITERATION && (
                    <div className='w-full p-20'>
                        {isTrajectoryLoading ? (
                            <OptimizationPipelineChartEmptyState>Loading iteration history...</OptimizationPipelineChartEmptyState>
                        ) : trajectoryErrorMessage ? (
                            <OptimizationPipelineChartEmptyState>Failed to load iteration history: {getPrettyError(trajectoryErrorMessage)}</OptimizationPipelineChartEmptyState>
                        ) : !trajectoryChartRows.length || !selectedTrajectoryKeys.length ? (
                            <OptimizationPipelineChartEmptyState>No iteration values available</OptimizationPipelineChartEmptyState>
                        ) : (
                            <OptimizationPipelineChartFrame ref={chartRef} height={520} overflowVisible>
                                <LineChart data={trajectoryChartRows} margin={{ top: 10, left: 10, right: 10, bottom: 10 }}>
                                    {trajectoryRoundBands.map((band) => (
                                        <ReferenceArea
                                            key={band.round + '_' + band.x1 + '_' + band.x2}
                                            x1={band.x1}
                                            x2={band.x2}
                                            fill={band.meta?.fill || 'rgba(148,163,184,0.12)'}
                                            stroke={band.meta?.stroke || 'rgba(148,163,184,0.24)'}
                                            ifOverflow='extendDomain'
                                        />
                                    ))}
                                    <CartesianGrid strokeDasharray='3 3' />
                                    <XAxis dataKey='overallIndex' ticks={trajectoryOverallIndexTicks} type='number' domain={['dataMin', 'dataMax']} allowDecimals={false} interval={0} />
                                    <YAxis />
                                    <Tooltip {...trajectoryTooltipProps} />

                                    {selectedTrajectoryKeys.map(key => (
                                        <Line key={key} dataKey={key} name={key} type={trajectoryLineType} stroke={colorForKey(key)} strokeWidth={getTrajectorySeriesStrokeWidth(key)} strokeOpacity={getTrajectorySeriesOpacity(key)} dot={false} connectNulls isAnimationActive={false} />
                                    ))}

                                    {trajectoryDatasetMode === 'variances' && showTrajectorySigma && selectedTrajectoryKeys.map(key => (
                                        <Line key={key + '__sigma'} dataKey={key + '__sigma'} name={key + ' sigma'} type={trajectoryLineType} stroke={colorForKey(key)} strokeWidth={getTrajectorySeriesStrokeWidth(key)} strokeOpacity={getTrajectorySeriesOpacity(key)} strokeDasharray='5 5' dot={false} connectNulls isAnimationActive={false} />
                                    ))}
                                </LineChart>
                            </OptimizationPipelineChartFrame>
                        )}
                    </div>
                )}
            </PopupBody>

        </OptimizationPipelinePopup>
    );
}
